import { useCallback, useMemo, useState, type ReactNode } from 'react';

import { ROLE_CAPABILITIES, type AdminCapability } from '@/lib/permissions';

import {
  OperatorContext,
  type ImpersonationState,
  type Operator,
  type OperatorContextValue,
} from './operator-context';

/**
 * Placeholder operator for local development.
 *
 * TODO(auth): replace with the `GET /me` response once Google Workspace SSO is
 * wired on mesh-admin-api. Capabilities must come from the server — deriving
 * them client-side is a UI convenience here only, and the API is the authority.
 */
const DEV_OPERATOR: Operator = {
  id: 'dev-operator',
  name: 'Local Operator',
  email: 'dev@mesh.local',
  role: 'owner',
  capabilities: ROLE_CAPABILITIES.owner,
};

export function OperatorProvider({ children }: { children: ReactNode }) {
  const [operator] = useState<Operator>(DEV_OPERATOR);
  const [impersonating, setImpersonating] = useState<ImpersonationState | null>(null);

  const capabilitySet = useMemo(
    () => new Set<AdminCapability>(operator.capabilities),
    [operator.capabilities],
  );

  const can = useCallback(
    (capability: AdminCapability) => capabilitySet.has(capability),
    [capabilitySet],
  );

  const stopImpersonating = useCallback(() => setImpersonating(null), []);

  const value = useMemo<OperatorContextValue>(
    () => ({ operator, impersonating, can, stopImpersonating }),
    [operator, impersonating, can, stopImpersonating],
  );

  return <OperatorContext value={value}>{children}</OperatorContext>;
}
